import * as amplitudeAnalytics from '@amplitude/analytics-browser';
import { initializeAmplitudeExperiment } from '../../experiments/amplitude-experiment-client';
import { buildGroupTraits, buildIdentifyTraits, isImpersonating } from './analytics-utility';

// The engagement SDK is loaded async from a CDN, so window.engagement frequently is not
// ready at the moment the user is identified (post-login). Poll for it instead of bailing.
const ENGAGEMENT_POLL_INTERVAL_MS = 250;
// Stop polling after ~30s — at that point the script is blocked (ad blocker) or failed to load.
const ENGAGEMENT_MAX_POLL_ATTEMPTS = 120;

const PAGE_VIEW_EVENT = 'Page View';

/**
 * Amplitude replaces Pendo for Guides, Surveys, and Experimentation.
 *
 * - Guides & Surveys: `@amplitude/engagement-browser` is loaded via CDN (see `amplitude-include.ejs`),
 *   exposing `window.engagement`. We boot it with the logged-in user's traits and forward its events
 *   into the existing Segment tracker so no new analytics pipeline is required.
 * - Experimentation: `@amplitude/experiment-js-client` is initialized via `initializeAmplitudeExperiment`.
 * - Page Views: `@amplitude/analytics-browser` sends a unified "Page View" event with the path as a
 *   property, matching the pattern used by Stamps. This avoids each unique URL becoming a distinct
 *   event type when page views flow through Segment.
 */
class AmplitudeClass {
  static booted = false;

  async identifyUser(userId: string, sellerId: string, userInfo: unknown, sellerInfo: unknown) {
    if (AmplitudeClass.booted || isImpersonating()) {
      return;
    }

    AmplitudeClass.booted = true;

    const userProperties: Record<string, unknown> = {
      ...buildIdentifyTraits(sellerId, userInfo, sellerInfo),
      ...buildGroupTraits(sellerId, sellerInfo),
      sellerId,
    };

    const apiKey = window.__amplitudeApiKey;
    if (apiKey) {
      amplitudeAnalytics.init(apiKey, userId, { defaultTracking: false });
    }

    // Boot experiment independently — it only needs the deployment key, not the engagement SDK.
    await initializeAmplitudeExperiment(userId, userProperties);

    if (window.engagement) {
      await this.bootEngagement(userId, userProperties);
      return;
    }

    this.waitForEngagementThenBoot(userId, userProperties);
  }

  private async bootEngagement(userId: string, userProperties: Record<string, unknown>) {
    if (!window.engagement) {
      return;
    }

    await window.engagement.boot({
      user: { user_id: userId, user_properties: userProperties },
      integrations: [
        {
          // Forward Engagement events into the existing Segment tracker
          track: (event) => window.analytics?.track(event.event_type, event.event_properties),
        },
      ],
    });
  }

  private waitForEngagementThenBoot(userId: string, userProperties: Record<string, unknown>) {
    let attempts = 0;

    const intervalId = setInterval(() => {
      attempts += 1;

      if (window.engagement) {
        clearInterval(intervalId);
        this.bootEngagement(userId, userProperties);
      } else if (attempts >= ENGAGEMENT_MAX_POLL_ATTEMPTS) {
        // The engagement script never became available — give up rather than poll forever.
        clearInterval(intervalId);
      }
    }, ENGAGEMENT_POLL_INTERVAL_MS);
  }

  trackPage(pageName: string) {
    amplitudeAnalytics.track(PAGE_VIEW_EVENT, { path: pageName });
  }

  // oxlint-disable-next-line no-empty-function
  trackEvent() {}
}

export const Amplitude = new AmplitudeClass();
